<template>
  <div class="media">
    <!-- Header -->
    <div class="monaco-card" style="margin: -24px -24px 24px -24px; border-radius: 8px 8px 0 0; border-left: none; border-right: none; border-top: none;">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <h1 class="text-3xl font-bold">Media Upload</h1>
        <p class="mt-2 text-gray-600">
          Upload and manage your photos and videos with our easy-to-use media uploader.
        </p>
      </div>
    </div>

    <!-- Main Content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Upload Section -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
        <h2 class="text-2xl font-semibold text-gray-900 mb-6">Upload Media</h2>
        
        <!-- File Upload Area -->
        <div
          @drop.prevent="handleDrop"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @change="handleFileSelect"
          class="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors duration-200"
          :class="{ 'border-blue-500 bg-blue-50': isDragging }"
        >
          <div class="flex flex-col items-center space-y-4">
            <div class="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
              <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.836V4a4 4 0 01-8 0H4a4 4 0 00-8 0v12.164a4 4 0 008.836 4.828V20a4 4 0 008.836 4.828A4 4 0 0016 16v-12.164A4 4 0 0024.828-4.828A4 4 0 0016 16v-12.164A4 4 0 008.836-4.828V4a4 4 0 00-8 0H4z"/>
              </svg>
            </div>
            <div>
              <p class="text-lg font-medium text-gray-900">
                Drop your files here, or click to browse
              </p>
              <p class="text-sm text-gray-500">
                Supports photos (JPG, PNG, GIF) and videos (MP4, WebM, MOV)
              </p>
            </div>
            <input
              ref="fileInput"
              type="file"
              accept="image/*,video/*"
              multiple
              @change="handleFileSelect"
              class="hidden"
            />
            <button
              @click="$refs.fileInput.click()"
              class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              Browse Files
            </button>
          </div>
        </div>

        <!-- Upload Progress -->
        <div v-if="uploadingFiles.length > 0" class="mt-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Upload Progress</h3>
          <div class="space-y-3">
            <div
              v-for="file in uploadingFiles"
              :key="file.id"
              class="bg-gray-50 rounded-lg p-4"
            >
              <div class="flex items-center justify-between mb-2">
                <div class="flex items-center space-x-3">
                  <div class="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                    <div
                      class="w-6 h-6 bg-blue-600 rounded-full animate-spin"
                      :style="{ animationDuration: '2s' }"
                    ></div>
                  </div>
                  <div>
                    <p class="font-medium text-gray-900">{{ file.name }}</p>
                    <p class="text-sm text-gray-500">{{ formatFileSize(file.size) }}</p>
                  </div>
                </div>
                <div class="flex items-center">
                  <div class="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      class="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      :style="{ width: `${file.progress}%` }"
                    ></div>
                  </div>
                  <span class="ml-3 text-sm text-gray-600">{{ file.progress }}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Media Gallery -->
      <div v-if="uploadedFiles.length > 0" class="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div class="flex justify-between items-center mb-6">
          <h2 class="text-2xl font-semibold text-gray-900">Media Gallery</h2>
          <button
            @click="clearAllFiles"
            class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
          >
            Clear All
          </button>
        </div>

        <!-- Filter Tabs -->
        <div class="flex space-x-4 mb-6 border-b border-gray-200">
          <button
            @click="activeTab = 'all'"
            :class="[
              'px-4 py-2 font-medium text-sm border-b-2 transition-colors duration-200',
              activeTab === 'all' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            ]"
          >
            All ({{ uploadedFiles.length }})
          </button>
          <button
            @click="activeTab = 'photos'"
            :class="[
              'px-4 py-2 font-medium text-sm border-b-2 transition-colors duration-200',
              activeTab === 'photos' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            ]"
          >
            Photos ({{ getFilesByType('image').length }})
          </button>
          <button
            @click="activeTab = 'videos'"
            :class="[
              'px-4 py-2 font-medium text-sm border-b-2 transition-colors duration-200',
              activeTab === 'videos' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            ]"
          >
            Videos ({{ getFilesByType('video').length }})
          </button>
        </div>

        <!-- Media Grid -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          <div
            v-for="file in filteredFiles"
            :key="file.id"
            class="group relative bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-200"
          >
            <!-- Media Preview -->
            <div class="aspect-w-16 bg-gray-100 relative">
              <img
                v-if="file.type.startsWith('image/')"
                :src="file.url"
                :alt="file.name"
                class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <video
                v-else-if="file.type.startsWith('video/')"
                :src="file.url"
                :alt="file.name"
                class="w-full h-full object-cover"
                controls
                preload="metadata"
              ></video>
              
              <!-- Overlay Actions -->
              <div class="absolute inset-0 bg-black bg-opacity-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center space-x-2">
                <button
                  @click="showPreview(file)"
                  class="p-2 bg-white rounded-full text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                  title="Preview"
                >
                  👁️
                </button>
                <button
                  @click="downloadFile(file)"
                  class="p-2 bg-white rounded-full text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                  title="Download"
                >
                  ⬇️
                </button>
                <button
                  @click="deleteFile(file)"
                  class="p-2 bg-red-500 rounded-full text-white hover:bg-red-600 transition-colors duration-200"
                  title="Delete"
                >
                  🗑️
                </button>
              </div>
            </div>

            <!-- File Info -->
            <div class="p-4">
              <h3 class="font-semibold text-gray-900 truncate">{{ file.name }}</h3>
              <p class="text-sm text-gray-500 mb-2">{{ formatFileSize(file.size) }}</p>
              <p class="text-xs text-gray-400">
                Uploaded {{ formatUploadTime(file.uploadTime) }}
              </p>
              <div class="flex flex-wrap gap-2 mt-2">
                <span
                  v-if="file.type.startsWith('image/')"
                  class="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                >
                  Photo
                </span>
                <span
                  v-else-if="file.type.startsWith('video/')"
                  class="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full"
                >
                  Video
                </span>
                <span class="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">
                  {{ getFileExtension(file.name) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-else
        class="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center"
      >
        <div class="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg class="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4 4m0 0l4 4m0-4l-4 4m-4-4l4 4m0 0l4 4"/>
          </svg>
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">No media uploaded yet</h3>
        <p class="text-gray-500">
          Upload your first photo or video to get started with the media gallery.
        </p>
        <button
          @click="$refs.fileInput.click()"
          class="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
        >
          Upload Your First Media
        </button>
      </div>
    </div>
  </div>

  <!-- Preview Modal -->
  <div
    v-if="currentPreview"
    class="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
    @click="currentPreview = null"
  >
    <div class="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-hidden">
      <div class="flex justify-between items-center p-4 border-b border-gray-200">
        <h3 class="text-lg font-semibold text-gray-900">
          Preview: {{ currentPreview.name }}
        </h3>
        <button
          @click="currentPreview = null"
          class="text-gray-400 hover:text-gray-600"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
      <div class="p-4">
        <div class="flex justify-center">
          <img
            v-if="currentPreview.type.startsWith('image/')"
            :src="currentPreview.url"
            :alt="currentPreview.name"
            class="max-w-full max-h-[60vh] object-contain"
          />
          <video
            v-else-if="currentPreview.type.startsWith('video/')"
            :src="currentPreview.url"
            :alt="currentPreview.name"
            controls
            class="max-w-full max-h-[60vh]"
          ></video>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const fileInput = ref(null)
const isDragging = ref(false)
const uploadingFiles = ref([])
const uploadedFiles = ref([])
const currentPreview = ref(null)
const activeTab = ref('all')

const handleDrop = (event) => {
  event.preventDefault()
  isDragging.value = false
  
  const files = Array.from(event.dataTransfer.files)
  uploadFiles(files)
}

const handleFileSelect = (event) => {
  const files = Array.from(event.target.files)
  uploadFiles(files)
}

const uploadFiles = async (files) => {
  for (const file of files) {
    const fileObj = {
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      file: file,
      progress: 0,
      url: null,
      uploadTime: new Date()
    }
    
    uploadingFiles.value.push(fileObj)
    
    // Simulate upload progress
    const progressInterval = setInterval(() => {
      const fileIndex = uploadingFiles.value.findIndex(f => f.id === fileObj.id)
      if (fileIndex !== -1) {
        const progress = Math.min(uploadingFiles.value[fileIndex].progress + 10, 100)
        uploadingFiles.value[fileIndex].progress = progress
        
        if (progress >= 100) {
          clearInterval(progressInterval)
          // Convert file to URL
          createFileUrl(fileObj).then(url => {
            fileObj.url = url
            fileObj.progress = 100
            
            // Move to uploaded files
            uploadedFiles.value.push(fileObj)
            uploadingFiles.value = uploadingFiles.value.filter(f => f.id !== fileObj.id)
          })
        }
      }
    }, 200)
  }
}

const createFileUrl = (fileObj) => {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      resolve(e.target.result)
    }
    reader.readAsDataURL(fileObj.file)
  })
}

const showPreview = (file) => {
  currentPreview.value = file
}

const downloadFile = (file) => {
  const link = document.createElement('a')
  link.href = file.url
  link.download = file.name
  link.click()
}

const deleteFile = (file) => {
  uploadedFiles.value = uploadedFiles.value.filter(f => f.id !== file.id)
}

const clearAllFiles = () => {
  uploadedFiles.value = []
}

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const formatUploadTime = (date) => {
  const now = new Date()
  const diff = now - date
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  
  if (days > 0) {
    return `${days} day${days > 1 ? 's' : ''} ago`
  } else if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''} ago`
  } else if (minutes > 0) {
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`
  } else {
    return 'Just now'
  }
}

const getFileExtension = (filename) => {
  return filename.split('.').pop().toUpperCase()
}

const getFilesByType = (type) => {
  return uploadedFiles.value.filter(file => file.type.startsWith(type))
}

const filteredFiles = computed(() => {
  if (activeTab.value === 'all') {
    return uploadedFiles.value
  }
  return getFilesByType(activeTab.value)
})
</script>

<style scoped>
.media {
  min-height: calc(100vh - 200px);
}

/* Custom animations */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 2s linear infinite;
}

/* Hover effects */
.group:hover .group-hover\:scale-105 {
  transform: scale(1.05);
}
</style>
